import { markTicketReady } from '@jq4race/api-client';
import type { RetrievalQueueItem } from '@jq4race/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { MY_ACTIVE_KEY } from './use-retrieval-queue';

/**
 * useMarkReady — post-payment-delay-recharge S1
 *
 * Wraps the PATCH /valet/tickets/:id/ready call.
 * On success: stamps `readyForPickupAt` into the MY_ACTIVE_KEY cache right away
 * (QA R2-03 — the ticket status stays in_transit, so without this the row would
 * not change until the next poll) and then invalidates it to re-sync
 * (idempotent — repeat calls are harmless).
 * On 409 error (ticket_already_delivered): also invalidates MY_ACTIVE_KEY so a
 * concurrently-delivered/cancelled ticket disappears from the list.
 */
export function useMarkReady() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => markTicketReady(id),
    onSuccess: (response) => {
      qc.setQueryData<RetrievalQueueItem[]>(MY_ACTIVE_KEY, (prev) =>
        prev?.map((item) =>
          item.id === response.id ? { ...item, readyForPickupAt: response.readyForPickupAt } : item,
        ),
      );
      void qc.invalidateQueries({ queryKey: MY_ACTIVE_KEY });
    },
    onError: (err) => {
      const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : null;
      if (code === 'ticket_already_delivered') {
        void qc.invalidateQueries({ queryKey: MY_ACTIVE_KEY });
      }
    },
  });
}
